import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { LegalEntity } from '../../objects/legal-entity';
import { LegalEntityService } from '../../service/entity-services/legal-entity.service';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';

import { AgGridModule } from 'ag-grid-angular';
import * as contractColumns from '../../shared/entity-columns/contract-columns';

@Component({
  selector: 'app-legal-entity',
  templateUrl: './legal-entity.component.html',
  styleUrls: ['./legal-entity.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AgGridModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatChipsModule
  ]
})
export class LegalEntityComponent implements OnInit, OnDestroy {
  
  // Inject services using modern pattern
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private legalEntityService = inject(LegalEntityService);

  // Component state
  currentLegalEntity: LegalEntity = {};
  userRole: string | null = null;
  reviewer = true;
  loading = true;

  // Grid state
  gridApi: any = null;
  contractsAssociatedWithThisLegalEntity: any[] = [];

  defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true
  };

  contractCols = [
    contractColumns.colContractId,
    contractColumns.colContractName,
    contractColumns.colType,
    contractColumns.colContractStatus,
    contractColumns.colContractLink
  ];

  // Subscriptions
  private subscriptions = new Subscription();

  constructor() {}

  ngOnInit() {
    // Get legal entity number from route params
    const routeSub = this.route.params.subscribe(params => {
      // The route parameter is named 'id' in the route config
      const legalEntityNum = params['id'];
      
      if (legalEntityNum) {
        console.log('Loading legal entity with number:', legalEntityNum);
        this.loadLegalEntity(legalEntityNum);
      } else {
        console.error('No legal entity number provided in route');
        this.loading = false;
      }
    });

    this.subscriptions.add(routeSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Load legal entity data from service
   */
  private loadLegalEntity(legalEntityNum: string) {
    this.loading = true;

    const legalEntitySub = this.legalEntityService.allLegalEntities$.subscribe({
      next: (legalEntities) => {
        const foundEntity = legalEntities.find(
          entity => entity.legalEntityNum === legalEntityNum
        );

        if (foundEntity) {
          this.currentLegalEntity = foundEntity;
          console.log('Loaded legal entity:', this.currentLegalEntity);
          
          // Load associated contracts if needed
          this.loadAssociatedContracts(legalEntityNum);
        } else {
          console.warn('Legal entity not found:', legalEntityNum);
        }
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading legal entity:', error);
        this.loading = false;
      }
    });

    this.subscriptions.add(legalEntitySub);
  }

  /**
   * Load contracts associated with this legal entity
   */
  private loadAssociatedContracts(legalEntityNum: string) {
    // TODO: Implement when contract service method is available
    // const contractsSub = this.contractService
    //   .getAssociatedContracts('legalEntities', legalEntityNum)
    //   .subscribe({
    //     next: (contracts) => {
    //       this.contractsAssociatedWithThisLegalEntity = contracts;
    //     },
    //     error: (error) => {
    //       console.error('Error loading associated contracts:', error);
    //     }
    //   });
    // 
    // this.subscriptions.add(contractsSub);
    
    console.log('Associated contracts loading not yet implemented');
  }

  /**
   * Navigate to edit mode
   */
  editLegalEntity() {
    if (this.currentLegalEntity.legalEntityNum) {
      // TODO: Implement edit mode - you'll need to pass the entity to edit
      // Option 1: Navigate with state
      this.router.navigate(['/securehome/new-legal-entity'], {
        state: { legalEntity: this.currentLegalEntity, editMode: true }
      });
      
      // Option 2: Navigate with route params
      // this.router.navigate(['/securehome/edit-legal-entity', this.currentLegalEntity.legalEntityNum]);
    }
  }

  /**
   * Navigate back to legal entities list
   */
  goBack() {
    this.router.navigateByUrl('/securehome/legal-entities');
  }

  /**
   * Handle grid cell clicks
   */
  onCellClicked(event: any) {
    console.log('Clicked on contract:', event.data);
    
    if (event.data.contractNum) {
      this.router.navigate(['/securehome/contract', event.data.contractNum]);
    }
  }

  /**
   * Handle grid ready event
   */
  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  /**
   * Helper getters for template
   */
  get isInactive(): boolean {
    return this.currentLegalEntity.inactive ?? false;
  }

  get canEdit(): boolean {
    return this.userRole !== 'Viewer';
  }

  get hasContracts(): boolean {
    return this.contractsAssociatedWithThisLegalEntity.length > 0;
  }
}